import React, { useState, useEffect } from "react";
import { useAuth } from '../components/context/AuthContext';
import { Page } from '../types';
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";
import { NotificationType } from "../App";
import { RocketIcon, TrashIcon } from "../components/Icons";
import PageWrapper from "../components/PageWrapper";
import { useWorkerAPI } from '../lib/worker-api';
import { deployToCloudflarePages } from '../lib/deployment-service';

// Token limits per plan
const TOKEN_LIMITS = {
  starter: { daily: 200, monthly: 3000 },
  creator: { daily: 400, monthly: 12000 },
  pro: { daily: 600, monthly: 30000 },
  business: { daily: 850, monthly: 100000 }
};

function getTokenLimit(type: 'daily' | 'monthly', plan: string): number {
  const normalizedPlan = plan.toLowerCase() as keyof typeof TOKEN_LIMITS;
  return TOKEN_LIMITS[normalizedPlan]?.[type] || TOKEN_LIMITS.starter[type];
}

interface DashboardPageProps {
  navigateTo: (page: Page, anchor?: string) => void;
  showNotification: (message: string, type: NotificationType) => void;
}

export default function DashboardPage({ navigateTo, showNotification }: DashboardPageProps) {
    const {
    user,
    profile,
    isLoading: authLoading,
    login,
    maxLiveProjects,
    getAccessToken
  } = useAuth();

    const workerAPI = useWorkerAPI();
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [deployingProjectId, setDeployingProjectId] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      login();
    }
  }, [authLoading, user, login]);

  // Fetch projects from API
  useEffect(() => {
    if (!authLoading && user) {
      setLoadingProjects(true);

      workerAPI.getProjects()
        .then((response) => {
          if (response && response.success && Array.isArray(response.data?.projects)) {
            // Sort by created_at descending (newest first)
            const sortedProjects = response.data.projects.sort((a: any, b: any) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return dateB - dateA;
            });
            setProjects(sortedProjects);
            setProjectsError(null);
          } else {
            setProjects([]);
            setProjectsError(null);
          }
        })
        .catch((error) => {
          console.error('Failed to load projects:', error);

          // Check if it's the RLS infinite recursion error
          const errorMessage = error?.message || String(error);
          if (errorMessage.includes('infinite recursion') || errorMessage.includes('42P17')) {
            setProjectsError('Database configuration error detected. Please contact support or check the Supabase RLS policies.');
            showNotification('Database error: Please run the RLS fix script in Supabase.', 'error');
          } else {
            setProjectsError('Failed to load projects. Please refresh the page or try again later.');
            showNotification('Failed to load projects. Please try again.', 'error');
          }
          setProjects([]);
        })
        .finally(() => {
          setLoadingProjects(false);
        });
    } else if (!authLoading) {
      setLoadingProjects(false);
    }
  }, [authLoading, user, showNotification]);

  const handleNewProject = () => {
    if (projects.length >= maxLiveProjects) {
        showNotification(`You have reached your project limit of ${maxLiveProjects}. Please upgrade your plan to create more projects.`, 'info');
        navigateTo('pricing');
        return;
    }
    navigateTo('workspace');
  };

  const handleEdit = (project: any) => {
    localStorage.setItem('codemine_editing_project', JSON.stringify(project));
    navigateTo('workspace');
  };

    const handleDelete = async (projectId: string) => {
    if (window.confirm('Are you sure you want to permanently delete this project? This action cannot be undone.')) {
      try {
        const response = await workerAPI.deleteProject(projectId);
        if (response.success) {
          const updatedProjects = projects.filter(p => p.id !== projectId);
          setProjects(updatedProjects);
          showNotification('Project deleted successfully.', 'success');
        } else {
          showNotification('Failed to delete project.', 'error');
        }
      } catch (error) {
        console.error('Failed to delete project:', error);
        showNotification('Failed to delete project. Please try again.', 'error');
      }
    }
  };

  const handleDeploy = async (project: any) => {
    // Check if user has deployment access (Creator, Pro, or Business)
    const planLower = (profile?.plan || 'starter').toLowerCase();
    if (planLower === 'starter') {
      showNotification('🔒 Upgrade to Creator plan or higher to deploy projects', 'info');
      navigateTo('pricing');
      return;
    }

    setDeployingProjectId(project.id);
    showNotification('🚀 Starting deployment...', 'info');

    try {
      // Deploy using worker API
      const result = await workerAPI.deployProject(project.id);

      if (result.success && result.data?.deployment) {
        const deployment = result.data.deployment;
        const deploymentUrl = deployment.url;
        showNotification(`✅ Deployed successfully! Live at: ${deploymentUrl}`, 'success');
        
        // Update project with deployment URL
        const updatedProjects = projects.map(p => 
          p.id === project.id ? { 
            ...p, 
            deployment_url: deploymentUrl, 
            subdomain: deployment.subdomain,
            status: 'deployed'
          } : p
        );
        setProjects(updatedProjects);
      } else {
        throw new Error(result.error || 'Deployment failed');
      }
    } catch (error) {
      console.error('Deployment error:', error);
      showNotification(`❌ Deployment failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setDeployingProjectId(null);
    }
  };

  if (authLoading || loadingProjects) return <div className="pt-28 flex items-center justify-center h-screen"><LoadingSpinner /></div>;
  
  if (!user) {
    return (
        <div className="pt-28 max-w-6xl mx-auto">
            <EmptyState
                title="Please Log In"
                message="You need to be logged in to access your dashboard."
                buttonText="Log In"
                onButtonClick={login}
            />
        </div>
    );
  }

  return (
    <PageWrapper>
      <div className="p-4 sm:p-8 max-w-7xl mx-auto w-full pt-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
                <h1 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)]">Dashboard</h1>
                {profile && <p className="text-[var(--text-secondary)] mt-1">Welcome back, {profile.name || profile.email}!</p>}
            </div>
            <button
                onClick={handleNewProject}
                disabled={projects.length >= maxLiveProjects}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 text-sm font-semibold bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
            >
                <RocketIcon />
                <span>Create New Project</span>
            </button>
        </div>

        {/* User Stats Section */}
        {profile && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[var(--panel-bg)]/50 border border-[var(--border-color)] rounded-xl p-6">
              <div className="text-sm text-[var(--text-secondary)] mb-1">Current Plan</div>
              <div className="text-2xl font-bold text-[var(--text-primary)] capitalize">{profile.plan || 'Starter'}</div>
            </div>
            <div className="bg-[var(--panel-bg)]/50 border border-[var(--border-color)] rounded-xl p-6">
              <div className="text-sm text-[var(--text-secondary)] mb-1">Projects</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{projects.length} / {maxLiveProjects}</div>
            </div>
            <div className="bg-[var(--panel-bg)]/50 border border-[var(--border-color)] rounded-xl p-6">
              <div className="text-sm text-[var(--text-secondary)] mb-1">Daily Tokens</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{profile.daily_tokens_used || 0} / {getTokenLimit('daily', profile.plan || 'starter')}</div>
            </div>
            <div className="bg-[var(--panel-bg)]/50 border border-[var(--border-color)] rounded-xl p-6">
              <div className="text-sm text-[var(--text-secondary)] mb-1">Monthly Tokens</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{profile.monthly_tokens_used || 0} / {getTokenLimit('monthly', profile.plan || 'starter')}</div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {projectsError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <div className="text-red-600 dark:text-red-400 text-xl">⚠️</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Error Loading Projects</h3>
                <p className="text-red-700 dark:text-red-400 text-sm mb-4">{projectsError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Projects Section */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">My Projects</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.length === 0 && !projectsError ? (
            <div className="col-span-full">
                <EmptyState
                  title="No Projects Yet"
                  message="You haven't created any projects. Let's build your first one!"
                  buttonText="Create First Project"
                  onButtonClick={handleNewProject}
                />
            </div>
          ) : projects.length === 0 && projectsError ? (
            <div className="col-span-full">
                <EmptyState
                  title="Unable to Load Projects"
                  message="There was an error loading your projects. Please try again or contact support if the problem persists."
                  buttonText="Retry"
                  onButtonClick={() => window.location.reload()}
                />
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="bg-[var(--panel-bg)]/50 border border-[var(--border-color)] rounded-xl p-6 flex flex-col hover:border-[var(--gradient-to)] transition-colors">
                <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2 line-clamp-1">
                        {project.name}
                    </h3>
                    <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2 h-10">
                        {project.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                        <span>📁 {project.file_count || 0} files</span>
                        <span>🛠️ {project.framework || project.project_type || 'web'}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                        Created: {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Unknown'}
                    </div>
                </div>
                                {project.deployment_url && (
                  <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-400">🌐 Live:</span>
                      <a 
                        href={project.deployment_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-green-400 hover:text-green-300 truncate flex-1"
                      >
                        {project.deployment_url}
                      </a>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2 mt-4">
                    <div className="flex items-center gap-2">
                      <button
                          onClick={() => handleEdit(project)}
                          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                      >
                          ✏️ Edit
                      </button>
                      <button
                          onClick={() => handleDeploy(project)}
                          disabled={deployingProjectId === project.id}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
                      >
                          {deployingProjectId === project.id ? '⏳ Deploying...' : project.deployment_url ? '🔄 Redeploy' : '🚀 Deploy'}
                      </button>
                      <button
                          onClick={() => handleDelete(project.id)}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                          aria-label="Delete project"
                      >
                          <TrashIcon />
                      </button>
                    </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PageWrapper>
  );
}